/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { Alert } from '../shared/Alert';
import { TwoFactorLogin } from '../shared/TwoFactorLogin';
import axios from '../../api/axios';
import { formatApiValidationErrors } from '../../utils/error.utils';
import { PasswordResetRequestDialog } from './PasswordResetRequestDialog';
import { isApplicationBrowserAuthMode } from '../../config/auth-principal-mode.config';
import { getApplicationAuthCopy } from './application-auth.copy';
import { getApplicationCodeForApi, getPlatformAppsUrl } from '../../config/app.config';
import { buildAppsLoginUrl } from '../../context/workspace-navigation.utils';
import { useOptionalTenant } from '../../context/TenantContext';

const API_PORT = import.meta.env.VITE_API_PORT || (import.meta.env.DEV ? '4001' : '4000');
const withApiPort = (message: string): string => message.replace(/\{apiUrl\}/g, `API port ${API_PORT}`);

const DB_UNAVAILABLE_PUBLIC_CODE = 'QP-DB-001';
const DB_UNAVAILABLE_CODE = 'TENANT_DATABASE_UNAVAILABLE';

/** DB-erişim uyarısı: SEÇİLİ DİLE göre (backend sabit-dil mesajı değil) + hata kodu. */
function buildDbUnavailableMessage(t: any, code?: string | null): string {
    const base =
        (import.meta.env.MODE === 'development' ? t.common?.databaseUnavailableDeveloperHint : '') ||
        t.common?.databaseUnavailableMessage ||
        t.login?.errors?.databaseUnavailable ||
        'Tenant database is unavailable. Please check VPN and database access.';
    return code && !String(base).includes(code) ? `${base} Hata kodu: ${code}` : base;
}

const EyeIcon = ({ open }: { open: boolean }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        {open ? (
            <>
                <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.6" />
            </>
        ) : (
            <>
                <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M10.58 6.14C11.04 6.05 11.51 6 12 6c5 0 9.27 3.11 11 7-.6 1.36-1.57 2.59-2.78 3.6M6.24 6.26C4.26 7.45 2.76 9.14 2 12c1.73 3.89 6 7 11 7 1.11 0 2.18-.14 3.18-.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.88 9.88c-.5.5-.81 1.19-.81 1.95 0 1.52 1.23 2.75 2.75 2.75.76 0 1.45-.31 1.95-.81" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </>
        )}
    </svg>
);

export interface LoginFormProps {
    isLocalDeployment?: boolean;
    onShowInitializing?: (show: boolean) => void;
    onError?: (err: any) => void;
    onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ isLocalDeployment = false, onShowInitializing, onError, onSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [spaceCode, setSpaceCode] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [passwordResetOpen, setPasswordResetOpen] = useState(false);
    const [secondFactor, setSecondFactor] = useState(false);

    const { login: authLogin, language, isInitialized, isAuthenticated, finishSecondFactor } = useAuth();
    const t = useTranslation(language);
    const applicationCopy = getApplicationAuthCopy(language);
    const applicationAuthMode = isApplicationBrowserAuthMode();
    const tenant = useOptionalTenant();
    const centralLogin = !isAuthenticated && !isLocalDeployment && !applicationAuthMode && getApplicationCodeForApi() !== 'APPS'
      ? buildAppsLoginUrl(getPlatformAppsUrl(), window.location.href + (tenant?.nativeAuthRequest && !window.location.hash ? `#native_request=${tenant.nativeAuthRequest}` : ''), getApplicationCodeForApi()) : null;
    useEffect(() => { if (centralLogin && isInitialized) window.location.replace(centralLogin); }, [centralLogin, isInitialized]);
    // Son hata DB-erişim ise hata kodunu tut → dil değişince uyarıyı yeniden türet.
    const dbUnavailableCodeRef = useRef<string | null>(null);
    useEffect(() => {
        if (dbUnavailableCodeRef.current !== null) {
            setError(buildDbUnavailableMessage(t, dbUnavailableCodeRef.current));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading || !isInitialized) return;
        setError('');
        setLoading(true);

        // Overlay artık timer tabanlı tahmine değil, backend'den gelen databaseInitializing
        // sinyaline göre AuthContext üzerinden yönetilmektedir (db-status polling).
        if (onShowInitializing) onShowInitializing(false);

        try {
            const emailTrimmed = email.trim();
            if (!emailTrimmed) {
                setError(t.login.emailRequired);
                setLoading(false);
                return;
            }

            if (!password) {
                setError(t.login.passwordRequired);
                setLoading(false);
                return;
            }

            const normalizedSpaceCode = spaceCode.trim();
            if (applicationAuthMode && isLocalDeployment && !normalizedSpaceCode) {
                setError(applicationCopy.spaceCodeRequired);
                setLoading(false);
                return;
            }

            const authenticated = await authLogin(emailTrimmed, password, {
                ...(normalizedSpaceCode ? { spaceCode: normalizedSpaceCode } : {}),
            });

            if (authenticated && onSuccess) {
                onSuccess();
            }
        } catch (err: any) {
            if (err.code === 'IDENTITY_SECOND_FACTOR_REQUIRED') { setPassword(''); setSecondFactor(true); return; }
            if (onShowInitializing) onShowInitializing(false);

            if (onError) onError(err);

            if (import.meta.env.MODE === 'development') {
                console.error('❌ Login error:', err);
            }

            const data = err.response?.data;
            const isDbUnavailable = err.response?.status === 503 && (
                data?.databaseUnavailable === true ||
                data?.publicCode === DB_UNAVAILABLE_PUBLIC_CODE ||
                data?.code === DB_UNAVAILABLE_CODE
            );

            let errorMessage: string;
            if (isDbUnavailable) {
                // DB erişilemiyor: SEÇİLİ DİLE göre mesaj (backend sabit-dil mesajını KULLANMA).
                // dbUnavailableCodeRef → dil değişince effect yeniden türetir.
                dbUnavailableCodeRef.current = data?.publicCode || data?.errorCode || DB_UNAVAILABLE_PUBLIC_CODE;
                errorMessage = buildDbUnavailableMessage(t, dbUnavailableCodeRef.current);
                setError(errorMessage);
                return;
            }

            dbUnavailableCodeRef.current = null;
            errorMessage = err.userMessage || err.response?.data?.error || err.response?.data?.message || err.message || t.login.loginFailed;

            const raw = String(errorMessage).toLowerCase();
            if ((raw.includes('veritabanı') && raw.includes('giriş')) || (raw.includes('database') && (raw.includes('unavailable') || raw.includes('sign in')))) {
                errorMessage = t.login.errors?.databaseUnavailable ?? errorMessage;
            } else if ((raw.includes('geçersiz') && (raw.includes('şifre') || raw.includes('kimlik'))) || (raw.includes('invalid') && (raw.includes('password') || raw.includes('credential')))) {
                errorMessage = t.login.errors?.invalidCredentials ?? errorMessage;
            } else if (raw.includes('aktif değil') || raw.includes('not active') || raw.includes('inactive')) {
                errorMessage = t.login.errors?.accountInactive ?? errorMessage;
            } else if ((raw.includes('oturum') && raw.includes('doldu')) || (raw.includes('session') && raw.includes('expir'))) {
                errorMessage = t.login.errors?.sessionExpired ?? errorMessage;
            } else if (raw.includes('çok fazla') || raw.includes('too many')) {
                errorMessage = t.login.errors?.tooManyAttempts ?? errorMessage;
            }

            if (err.response?.status === 503) {
                const errorData = err.response?.data;
                errorMessage = errorData?.message || errorData?.error || err.userMessage || t.login.backendUnavailable;
            }

            if (!err.response && (err.code === 'ECONNREFUSED' || err.code === 'ETIMEDOUT' || err.message?.includes('Proxy error'))) {
                errorMessage = import.meta.env.MODE === 'development'
                    ? withApiPort(t.login.backendConnectFailedDev)
                    : t.login.backendConnectFailed;
            }

            if (err.response?.data?.errors != null) {
                const errorDetails = formatApiValidationErrors(err.response.data.errors);
                if (errorDetails) {
                    errorMessage = `${errorMessage} - ${errorDetails}`;
                }
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (centralLogin) return <div role="status" className="workspace-chooser-state">{t.login.loggingIn}</div>;
    if (secondFactor) return <TwoFactorLogin locale={language}
      request={async (action, body, signal) => (await axios.post(`/api/auth/two-factor/${action}`, body, { signal, validateStatus: () => true })).data}
      onComplete={finishSecondFactor} onBack={() => { setSecondFactor(false); setPassword(''); }} />;
    return (
        <>
        <form onSubmit={handleSubmit} className="login-form">
            <Alert type="error" message={error} onDismiss={() => setError('')} />

            <div className="form-group">
                <label htmlFor="email">{t.login.emailLabel} *</label>
                <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder={t.login.emailPlaceholderFull}
                    autoComplete={isLocalDeployment ? 'section-qubepro-local username' : 'email'}
                />
            </div>

            {applicationAuthMode && isLocalDeployment ? (
                <div className="form-group">
                    <label htmlFor="application-space-code">
                        {applicationCopy.spaceCodeLabel} *
                    </label>
                    <input
                        id="application-space-code"
                        type="text"
                        value={spaceCode}
                        onChange={(e) => setSpaceCode(e.target.value)}
                        required
                        autoComplete="organization"
                        maxLength={80}
                    />
                </div>
            ) : null}

            <div className="form-group">
                <label htmlFor="password">{t.login.passwordLabel} *</label>
                <div className="password-input-wrapper">
                    <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder={t.login.passwordPlaceholderFull}
                        autoComplete={isLocalDeployment ? 'section-qubepro-local current-password' : 'current-password'}
                    />
                    <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? t.login.hidePassword : t.login.showPassword}
                        title={showPassword ? t.login.hidePassword : t.login.showPassword}
                    >
                        <EyeIcon open={showPassword} />
                    </button>
                </div>
                <div className="password-reset-trigger-row">
                    <button
                        type="button"
                        className="password-reset-trigger"
                        onClick={() => setPasswordResetOpen(true)}
                    >
                        {t.passwordReset?.selfServiceOpen ?? (language === 'tr' ? 'Şifremi unuttum' : 'Forgot password')}
                    </button>
                </div>
            </div>

            <button type="submit" disabled={loading || !isInitialized} className="login-button">
                {loading || !isInitialized ? t.login.loggingIn : t.login.submitButton}
            </button>
        </form>
        <PasswordResetRequestDialog
            isOpen={passwordResetOpen}
            initialEmail={email}
            onClose={() => setPasswordResetOpen(false)}
        />
        </>
    );
};
